import { useIntl } from '@umijs/max';
import { Popover, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import { useState, type CSSProperties } from 'react';
import { TouchSafeTooltip } from '../TouchSafeTooltip';
import { classifyTaskFailure } from '../../utils/taskFailureReason';
import { storedUserErrorCode, storedUserErrorMessage } from '../../utils/userErrorMessage';

const useStyles = createStyles(({ token, css }) => ({
  inline: css`
    font-size: 11px;
    color: ${token.colorErrorText};
    line-height: 1.45;
    word-break: break-word;
  `,
  tag: css`
    margin-inline-end: 0;
  `,
  trigger: css`
    display: inline-flex;
    max-width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    pointer-events: auto;
    &:focus-visible {
      outline: 2px solid ${token.colorPrimary};
      outline-offset: 2px;
    }
  `,
  tech: css`
    max-width: min(360px, calc(100vw - 48px));
    white-space: pre-wrap;
    word-break: break-word;
  `,
}));

export interface TaskFailureReasonProps {
  raw?: string | null;
  code?: string | null;
  mode?: 'inline' | 'tag';
  showTechnical?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function TaskFailureReason({
  raw,
  code,
  mode = 'inline',
  showTechnical = false,
  className,
  style,
}: TaskFailureReasonProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const { styles, cx } = useStyles();
  const info = classifyTaskFailure(raw, code);
  if (!info) return null;

  const message = intl.formatMessage(
    { id: `taskFailure.${info.kind}` },
    { balance: info.balance ?? '' },
  );
  const retryHint = info.retryable
    ? intl.formatMessage({ id: 'taskFailure.retryHint' })
    : '';
  const approvedMessage = storedUserErrorMessage(raw);
  const text = approvedMessage || intl.formatMessage({
    id: `taskFailure.detail.${info.kind}`,
    defaultMessage: retryHint ? `${message} — ${retryHint}` : message,
  });
  const label = approvedMessage && (code || storedUserErrorCode(raw)) === 'D214'
    ? intl.formatMessage({ id: 'referenceError.summary' })
    : message;
  const title = showTechnical ? (
    <div className={styles.tech}>{info.raw}</div>
  ) : (
    text
  );

  if (mode === 'tag') {
    return (
      <Popover
        content={<div className={styles.tech}>{title}</div>}
        placement="top"
        trigger={['hover', 'click']}
        open={open}
        onOpenChange={setOpen}
      >
        <button
          type="button"
          className={cx(styles.trigger, 'nodrag', 'nopan')}
          aria-label={intl.formatMessage({ id: 'referenceError.viewReason' }, { reason: text })}
          aria-expanded={open}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
        >
          <Tag color="error" className={cx(styles.tag, className)} style={style}>
            {label}
          </Tag>
        </button>
      </Popover>
    );
  }

  return (
    <TouchSafeTooltip title={title} placement="topLeft">
      <Typography.Text className={cx(styles.inline, className)} style={style}>
        {text}
      </Typography.Text>
    </TouchSafeTooltip>
  );
}

export default TaskFailureReason;
